<template>
    <Drawer ref="drawer" title="New Budget Entry" :loading="isSubmitting" size="is-small">
        <div class="tw-grid tw-gap-3">
            <FormField label="Title">
                <Textbox v-model="form.name" maxlength="255" placeholder="Title" />
            </FormField>
            <FormField label="Department">
                <Selector v-model="form.department_id" :options="departments" placeholder="Select a department" />
            </FormField>
            <FormField v-if="!!form.department_id" label="Subcategory">
                <Selector
                    v-model="form.category_id"
                    :options="categories"
                    :all-options="categories"
                    placeholder="Subcategory"
                />
            </FormField>
            <FormField label="Supplier">
                <SupplierSelector v-model="form.supplier_id" class="!tw-w-fit" />
            </FormField>
            <Currency
                v-model="form.fixed_cost"
                :currency-code="store.get('current_event.currency_code', store.get('account.currency_code'))"
                :min="0"
                :step="0.01"
                label="Fixed Cost"
            />
            <Currency
                v-model="form.budgeted_cost"
                :currency-code="store.get('current_event.currency_code', store.get('account.currency_code'))"
                :min="0"
                :step="0.01"
                label="Budgeted Cost"
            />
        </div>

        <template #footer>
            <div class="tw-flex tw-justify-between">
                <Button @click="close()">Cancel</Button>
                <Button
                    :disabled="!isValid || isSubmitting"
                    type="is-info"
                    @click="
                        submit({
                            onCreated,
                        })
                    "
                >
                    Create Budget Entry
                </Button>
            </div>
        </template>
    </Drawer>
</template>

<script lang="ts" setup>
import Drawer from '@/js/components/Drawer.vue';
import Button from '@/js/components/controls/Button.vue';
import { Eventbus } from '@/js/utils/eventbus.ts';
import { computed, nextTick, onMounted, useTemplateRef } from 'vue';
import { useDataStore } from '@/js/stores/DataStore.js';
import { useCreateBudgetEntry } from '@/js/composables/useCreateBudgetEntry.ts';
import FormField from '@/js/components/widgets/FormField.vue';
import Selector from '@/js/components/controls/Selector.vue';
import SupplierSelector from '@/js/components/controls/SupplierSelector.vue';
import Currency from '@/js/components/controls/Currency.vue';
import type { Department } from '@/js/types/department.ts';
import type { Category } from '@/js/types/category.ts';
import Textbox from '@/js/components/controls/Textbox.vue';

const drawer = useTemplateRef('drawer');
const store = useDataStore();
const { form, submit, isValid, isSubmitting } = useCreateBudgetEntry();

const departments = computed<Department[]>(() => {
    return [
        ...Object.values(store.get('current_event.revenue_departments') as Record<string, Department>).filter(
            (department) => department.revenue_stream_type !== 0
        ),
        ...Object.values(store.get('current_event.cost_departments') as Record<string, Department>),
    ];
});
const categories = computed<Category[]>(() => {
    const department = departments.value.find((department) => department.id === form.department_id);
    return Object.values(department?.categories ?? {});
});

onMounted(() => {
    Eventbus.$on('newBudgetEntry:open', async (payload) => {
        drawer.value?.open();
        await nextTick(); // needed to ensure form is rendered before we can set values
        if (payload?.department) {
            form.department_id = payload.department;
        }
        if (payload?.category) {
            form.category_id = payload.category;
        }
    });
});

const close = () => {
    drawer.value?.close();
};

const onCreated = (entry: { event_id: number; id: number }) => {
    Eventbus.$emit('budget:entry', {
        event: entry.event_id,
        entry: entry.id,
    });
    close();
};
</script>
